import React, {createRef, ReactElement, RefObject} from 'react';
import MComponent from '../../Toolkit/Components/Component';
import {Column} from 'primereact/column';
import MTable, {MTableFilters} from '../../Toolkit/Components/Controls/Table';
import {SafeDigFile, UpdateSafeDigFileReply} from '../../Api/SafeDigFileTypes';
import MButton from '../../Toolkit/Components/Controls/Button';
import MToolbar from '../../Toolkit/Components/Controls/Toolbar';
import {MNewIconName} from '../../Toolkit/Icons';
import FileUploadDialog from '../Dialogs/FileUploadDialog';
import {FileTypeNames, FileStatusNames, ArchiveIconName, DownloadIconName} from '../SafeDigConstants';
import StatusIndicator from './StatusIndicator';
import MSpacer from '../../Toolkit/Components/Controls/Spacer';
import {DataTableSortMeta} from 'primereact/datatable';
import NSwagAdapter from '../../Toolkit/Api/NSwagAdapter';
import {SafeDigFileClient} from '../../Api/SafeDigFilesClients';
import EnumContext from '../../Context/enum-context';

type SafeDigFileRow = {
    data: SafeDigFile;
}

type Props = {
    rowData: SafeDigFileRow[];
    updateIncludeState: (safeDigFileId: number, newValue: boolean) => void;
    addFile: (fileType: number) => void;
    refresh: () => void;
    fileClient: NSwagAdapter<SafeDigFileClient>;
    jobComplete: boolean;
    user: string;
    uploadPermission: boolean;
    role: string[]
}

/** */
type State = {
    filters: MTableFilters;
    selectedRow?: SafeDigFileRow;
}

class FileListControl extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>

    private readonly m_uploadRef: RefObject<FileUploadDialog>;

    public constructor(props: Props) {
        super(props);

        this.m_uploadRef = createRef<FileUploadDialog>();

        this.bindMethods();
        this.initializeState();
    }

    private bindMethods(): void {
        this.selectTemplate = this.selectTemplate.bind(this);
        this.fileTypeTemplate = this.fileTypeTemplate.bind(this);
        this.isArchiveDisabled = this.isArchiveDisabled.bind(this);
        this.new_Click = this.new_Click.bind(this);
        this.archive_Click = this.archive_Click.bind(this);
        this.download_Click = this.download_Click.bind(this);
        this.table_onSelectionChange = this.table_onSelectionChange.bind(this);
    }

    private initializeState(): void {
        this.state = {
            filters: {}
        };
    }

    private isArchiveDisabled(): boolean {
        if(this.state.selectedRow === undefined) {
            return true;
        }
        return this.state.selectedRow.data.fileStatus === 3;
    }

    private table_onSelectionChange(selection: SafeDigFileRow | SafeDigFileRow[] | undefined): void {

        this.safeSetState((draft: State) =>
            draft.selectedRow = selection as SafeDigFileRow | undefined);

    }

    private new_Click(): void {
        this.props.addFile(7);
    }

    private async archive_Click(): Promise<void> {
        const archiveReply = await this.props.fileClient.callWithReply(
            UpdateSafeDigFileReply,
            (client) => client.archiveFile(this.state.selectedRow!.data!.safeDigFilesId, this.props.user));

        archiveReply.throwIfFailed();

        this.props.refresh();
    }

    private async download_Click(): Promise<void> {
        const downloadReply = await this.props.fileClient.call(
            (client) => client.downloadFile(this.state.selectedRow!.data!.safeDigFilesId));

        downloadReply.throwIfFailed();
    }

    private select_Click(lineData: SafeDigFile): void {
        this.props.updateIncludeState(lineData.safeDigFilesId, !lineData.includeInPrint);
    }

    private fileTypeTemplate(rowData: SafeDigFileRow): string | undefined {
        if(rowData.data.fileArea === 0) {
            return FileTypeNames[rowData.data.fileType];
        } else if(rowData.data.fileArea === 1) {
            const index = this.context?.enums?.noticeFileTypes.findIndex(n => n.id === rowData.data.fileType)
            return index === undefined ? '' : this.context?.enums?.noticeFileTypes[index]?.name ?? '';
        } else {
            return '';
        }
    }

    private fileStatusTemplate(rowData: SafeDigFileRow): string | undefined {
        return FileStatusNames[rowData.data.fileStatus];
    }

    private expiryDateTemplate(rowData: SafeDigFileRow): string | undefined {
        if(rowData.data.expiryDate !== null) {
            try {
                return rowData.data.expiryDate!.toLocaleDateString();
            } catch {
                return '';
            }
        }
        return '';
    }

    private fileStatusIconTemplate(rowData: SafeDigFileRow): ReactElement {
        let statusValue = 1;
        const currentDate = new Date();
        var futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + 7);

        if(rowData.data.fileStatus === 2 && rowData.data.expiryDate >= currentDate) {
            statusValue = 2;
        }

        if (rowData.data.fileStatus === 2 && rowData.data.expiryDate < futureDate && rowData.data.expiryDate > currentDate)
        {
            statusValue = 6;
        }
        
        return <StatusIndicator size={25} status={statusValue}/>;
    }

    private selectTemplate(rowData: SafeDigFileRow): ReactElement {
        return <MButton label={rowData.data.includeInPrint ? 'Yes' : 'No'}
                        color={rowData.data.includeInPrint === true ? 'success' : 'primary'}
                        disabled={false}
                        onClick={this.select_Click.bind(this, rowData.data)}/>;
    }

    private tableSortTemplate(): DataTableSortMeta[] {
        return [
            {field: 'data.fileStatus', order: 1},
            {field: 'data.fileType', order: 1}
        ];
    }

    private checkFileExpiry(): boolean {
        const today = new Date();
        var rolePlanning = this.props.role.includes('SafeDigs.Planning')
        if (this.state.selectedRow === undefined || (this.state.selectedRow!.data!.expiryDate < today && rolePlanning))
        {
            return true;
        }
        return false;
    }

    public render(): ReactElement {
        return (
            <div>
                <FileUploadDialog ref={this.m_uploadRef}
                                  jobComplete={this.props.jobComplete}
                                  uploadPermission={this.props.uploadPermission}
                />
                <MToolbar>
                    <MButton label="Add File"
                             color="success"
                             icon={MNewIconName}
                             disabled={false}
                             onClick={this.new_Click}/>
                    <MSpacer/>
                    <MButton label="Archive"
                             icon={ArchiveIconName}
                             color="success"
                             disabled={this.isArchiveDisabled}
                             onClick={this.archive_Click}/>
                    <MSpacer/>
                    <MButton label="Download"
                             icon={DownloadIconName}
                             disabled={this.checkFileExpiry()}
                             onClick={this.download_Click}/>
                </MToolbar>
                <MTable
                    rowKey="data.safeDigFilesId"
                    rowData={this.props.rowData}
                    filters={this.state.filters}
                    onSelectionChange={this.table_onSelectionChange}
                    sortMode={'multiple'}
                    multiSortMeta={this.tableSortTemplate()}>

                    <Column field="data.fileType"
                            header="File Type"
                            body={this.fileTypeTemplate}
                            filter={false}
                            sortable={true}
                            style={{width: '15%'}}/>
                    <Column field="data.fileInfo"
                            header="File Info"
                            filter={false}
                            sortable={false}
                            style={{width: '50%'}}/>
                    <Column field="data.fileName"
                            header="File Name"
                            filter={false}
                            sortable={false}
                            style={{width: '15%'}}/>
                    <Column field="data.fileStatus"
                            header="File Status"
                            body={this.fileStatusTemplate}
                            filter={false}
                            sortable={true}
                            style={{width: '15%'}}/>
                    <Column field="data.expiryDate"
                            header="Expiry Date"
                            body={this.expiryDateTemplate}
                            filter={false}
                            sortable={true}
                            style={{width: '15%'}}/>
                    <Column header="Valid"
                            body={this.fileStatusIconTemplate}
                            filter={false}
                            sortable={true}
                            style={{width: '15%'}}/>
                    <Column header="Include"
                            body={this.selectTemplate}
                            filter={false}
                            sortable={false}
                            style={{width: '15%'}}/>
                </MTable>
            </div>
        );
    }
}

export default FileListControl;